import { useState, useEffect } from 'react'
import { Box, Typography, IconButton, Tooltip, Divider, CircularProgress } from '@mui/material'
import {
  Google,
  Facebook,
  Apple,
  LinkedIn,
  Twitter,
  GitHub,
  Instagram,
} from '@mui/icons-material'
import { FaMicrosoft, FaDiscord } from 'react-icons/fa'
import { authApi } from '@api/endpoints/auth'

interface SocialLoginButtonsProps {
  onProviderClick: (provider: string) => void
  isLoading?: boolean
}

// Provider icon and color mapping
const providerConfig: Record<string, { icon: React.ReactElement; color: string }> = {
  Google: { icon: <Google />, color: '#4285F4' },
  Facebook: { icon: <Facebook />, color: '#1877F2' },
  Microsoft: { icon: <FaMicrosoft size={24} />, color: '#00A4EF' },
  Instagram: { icon: <Instagram />, color: '#E4405F' },
  Twitter: { icon: <Twitter />, color: '#1DA1F2' },
  Apple: { icon: <Apple />, color: '#000000' },
  LinkedIn: { icon: <LinkedIn />, color: '#0A66C2' },
  Discord: { icon: <FaDiscord size={24} />, color: '#5865F2' },
  GitHub: { icon: <GitHub />, color: '#333333' },
}

export function SocialLoginButtons({ onProviderClick, isLoading = false }: SocialLoginButtonsProps) {
  const [providers, setProviders] = useState<string[]>([])
  const [isLoadingProviders, setIsLoadingProviders] = useState(true)

	useEffect(() => {
	  const fetchProviders = async () => {
	    try {
	      const response = await authApi.getOAuthProviders()
	      // Safely handle null/undefined
	      const enabledProviders = (response.providers ?? [])
		.filter(p => p.isEnabled)
		.map(p => p.name)

	      setProviders(enabledProviders)
	    } catch (error) {
	      console.error('Failed to fetch OAuth providers:', error)
	      setProviders([])
	    } finally {
	      setIsLoadingProviders(false)
	    }
	  }

	  fetchProviders()
	}, [])

  // Don't show anything if no providers are enabled
  if (isLoadingProviders) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (providers.length === 0) {
    return null
  }

  return (
    <Box>
      <Divider sx={{ my: 3 }}>
        <Typography variant="body2" color="text.secondary">
          OR
        </Typography>
      </Divider>

      <Typography variant="body2" align="center" sx={{ mb: 2, fontWeight: 500 }}>
        Login with your favorite platform
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 2,
          justifyItems: 'center',
        }}
      >
        {providers.map((providerName) => {
          const config = providerConfig[providerName]
          if (!config) return null

          return (
            <Tooltip key={providerName} title={`Login with ${providerName}`}>
              <IconButton
                onClick={() => onProviderClick(providerName)}
                disabled={isLoading}
                sx={{
                  width: 56,
                  height: 56,
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: `${config.color}10`,
                    transform: 'scale(1.1)',
                  },
                  '& svg': {
                    color: config.color,
                    fontSize: 32,
                  },
                }}
              >
                {config.icon}
              </IconButton>
            </Tooltip>
          )
        })}
      </Box>
    </Box>
  )
}
